/**
 * Проигрывание звуков саундборда.
 *
 * НЕ ВИДЖЕТ, и это принципиально. Виджет — это слой: он живёт
 * на сцене, у него есть прямоугольник, его двигают и выключают.
 * Звук саундборда не живёт нигде: он случается и заканчивается сам
 * (docs/04). Заводить ради него слой значило бы завести объект,
 * который надо не забыть убрать, — и однажды его не уберут.
 *
 * ЧЕТЫРЕ ВЕЩИ, КОТОРЫЕ ЗДЕСЬ ДЕЛАЮТСЯ, И КАЖДАЯ ОТ СВОЕЙ БЕДЫ:
 *
 *   прогретый буфер — иначе `<audio>` даёт 100–300 мс до первого
 *                     звука, и реакция опаздывает к моменту;
 *   мимо ducking    — иначе звук приглушал бы сам себя;
 *   выравнивание    — иначе один звук не слышно, другой рвёт колонки;
 *   уборка узлов    — иначе за часы эфира граф зарастает мёртвыми
 *                     источниками, и звук начинает заикаться.
 */
import { audioGraph } from './audio.js';
import { warmCache } from './warm.js';
/**
 * До какой доли приглушается фон на время звука.
 *
 * Не до нуля: полная тишина фона слышна как обрыв, и зритель решит,
 * что трансляция подвисла. Треть — это «звук вышел вперёд», а не
 * «музыку выключили».
 */
export const DUCK_TO = 0.35;
/**
 * Сколько держать фон приглушённым, когда длина неизвестна.
 *
 * Две с половиной секунды — длина типичного звука саундборда.
 * Ошибиться в эту сторону безопаснее: фон вернётся чуть позже,
 * а не посреди звука.
 */
export const DEFAULT_DUCK_MS = 2500;
/**
 * Запас после конца звука, мс.
 *
 * Фон возвращается не встык: возврат громкости ровно в момент
 * последнего сэмпла слышен как щелчок.
 */
export const DUCK_TAIL_MS = 300;
/**
 * Проигрыватель саундборда.
 *
 * Классом, а не функцией: звучащие сейчас элементы надо где-то
 * держать, чтобы убрать за ними, а модульный список пережил бы
 * закрытие страницы предпросмотра в панели.
 */
export class SoundPlayer {
    graph;
    createAudio;
    warmed;
    /**
     * Звучащее прямо сейчас.
     *
     * Нужен не для учёта, а для уборки: узлы Web Audio не собираются
     * сборщиком мусора, пока подключены, и за час эфира их набегают
     * сотни.
     */
    playing = new Set();
    /**
     * Остановки звуков, играющих из прогретых буферов.
     *
     * Отдельно от `playing`: у буфера нет элемента, за которым надо
     * убирать, — есть только «остановить». Общий список заставил бы
     * держать половину полей пустыми.
     */
    buffered = new Set();
    constructor(options = {}) {
        this.graph = options.graph ?? audioGraph;
        this.createAudio = options.createAudio ?? ((url) => new Audio(url));
        this.warmed = options.warmed ?? warmedFromCache;
    }
    /**
     * Играет звук.
     *
     * Возвращает false, если играть нечем или нечего. Молча — потому
     * что молчание тут единственный разумный исход: оверлей обязан
     * оставаться рабочим оверлеем и без звука.
     */
    play(request) {
        if (request.url === '')
            return false;
        const graph = this.graph();
        if (graph === null)
            return false;
        const level = clamp(request.gain) * boost(request.loudnessGain);
        // ПРОГРЕТЫЙ БУФЕР — ПЕРВЫМ ДЕЛОМ. Ради этого прогрев и заведён:
        // `<audio>` начал бы звучать через 100–300 мс, и реакция
        // опоздала бы к моменту, ради которого кнопку нажали
        const warm = this.warmed(request.url);
        if (warm !== null && this.playWarm(graph, warm, level, request.id ?? '')) {
            return true;
        }
        const el = this.createAudio(request.url);
        // Наша раздача — чужой origin для страницы оверлея, и без CORS
        // граф получит от неё ТИШИНУ: браузер подключит источник,
        // всё будет выглядеть рабочим, а звука не будет
        el.crossOrigin = 'anonymous';
        const route = graph.connectDirect(el);
        if (route === null) {
            // Граф отказал. Играть через сам элемент нельзя: это дорога
            // мимо мастера, то есть мимо потолка владельца (docs/05)
            return false;
        }
        route.setGain(level);
        const entry = { id: request.id ?? '', el, route };
        this.playing.add(entry);
        const done = () => {
            this.finish(entry);
        };
        el.addEventListener('ended', done, { once: true });
        // Файл мог не скачаться: без этого мёртвый элемент остался бы
        // в графе навсегда, а таких за эфир накапливается столько,
        // сколько раз хранилище моргнуло
        el.addEventListener('error', done, { once: true });
        // Приглушение ставится ДО play: отказ в проигрывании вернёт фон
        // по сроку, а вот поднятый после долгой загрузки фон дал бы
        // приглушение уже после звука
        graph.duck({ to: DUCK_TO, holdMs: duckWindow(request.durationMs) });
        // Отказ игнорируется: браузер вправе не дать играть без жеста.
        // В Browser Source жестов не бывает вовсе, и настаивать
        // бессмысленно — контекст будит оверлей сам
        void el.play().catch(done);
        return true;
    }
    /** Сколько звуков звучит сейчас. Нужно тестам и отладке. */
    get active() {
        return this.playing.size + this.buffered.size;
    }
    /**
     * Затыкает один звук — тот, что запустили этим нажатием.
     *
     * ЗАЧЕМ. Нажатие уходило в эфир и пропадало из виду: остановить
     * трёхминутный трек, запущенный не тем движением, было нечем.
     * Оставался стоп-кран — то есть тишина всего эфира из-за одной
     * кнопки.
     *
     * Отвечает, нашлось ли что останавливать. Не нашлось — не ошибка:
     * звук мог доиграть сам, пока просьба шла по сети.
     */
    stop(id) {
        if (id === '')
            return false;
        let found = false;
        for (const entry of [...this.playing]) {
            if (entry.id !== id)
                continue;
            entry.el.pause();
            this.finish(entry);
            found = true;
        }
        for (const entry of [...this.buffered]) {
            if (entry.id !== id)
                continue;
            entry.stop();
            this.buffered.delete(entry);
            found = true;
        }
        return found;
    }
    /**
     * Убирает всё разом.
     *
     * Для закрытия страницы предпросмотра: оставленные узлы держат
     * контекст живым, а вкладок панели за смену открывают много.
     */
    stopAll() {
        for (const entry of [...this.playing]) {
            entry.el.pause();
            this.finish(entry);
        }
        for (const entry of [...this.buffered]) {
            entry.stop();
        }
        this.buffered.clear();
    }
    /** Играет из прогретого буфера. Возвращает, получилось ли. */
    playWarm(graph, buffer, level, id) {
        // Держатель, а не переменная: короткий звук способен кончиться
        // ВНУТРИ playBuffer, до того как нам вернут «остановить». Без
        // этой развязки такой звук остался бы в списке звучащих навсегда
        const held = { entry: null };
        let ended = false;
        const stop = graph.playBuffer({
            buffer,
            gain: level,
            onEnded: () => {
                ended = true;
                if (held.entry !== null)
                    this.buffered.delete(held.entry);
            },
        });
        if (stop === null)
            return false;
        const entry = { id, stop };
        held.entry = entry;
        if (!ended)
            this.buffered.add(entry);
        // Длина известна ТОЧНО — она в самом буфере, а не со слов
        // сервера. Приглушение фона получается ровно под звук
        graph.duck({ to: DUCK_TO, holdMs: duckWindow(buffer.duration * 1000) });
        return true;
    }
    finish(entry) {
        if (!this.playing.delete(entry))
            return;
        entry.route.release();
        // Источник обнуляется явно: браузер продолжает докачивать файл
        // по брошенному элементу, а это полоса стримера во время эфира
        entry.el.removeAttribute('src');
    }
}
/** Сколько держать фон приглушённым под звук такой длины. */
export function duckWindow(durationMs) {
    if (typeof durationMs !== 'number' || !Number.isFinite(durationMs) || durationMs <= 0) {
        return DEFAULT_DUCK_MS;
    }
    return durationMs + DUCK_TAIL_MS;
}
function clamp(value) {
    if (!Number.isFinite(value))
        return 0;
    return Math.min(1, Math.max(0, value));
}
/** Прогретый звук из общего кеша страницы. */
function warmedFromCache(url) {
    const entry = warmCache()?.get(url) ?? null;
    return entry !== null && entry.kind === 'sound' ? entry.buffer : null;
}
function boost(value) {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0)
        return 1;
    return value;
}
//# sourceMappingURL=sound.js.map